import { useCallback, useEffect, useRef } from 'react'

import { ITranscriptions } from '@/types/task.interface'

import { findClosestIndexByStart } from '../helpers'

export const useTranscriptionScrollSync = (
	transcriptions: ITranscriptions | null
) => {
	const containerRef = useRef<HTMLDivElement>(null)
	const translatedRef = useRef<HTMLDivElement>(null)

	const originalRefs = useRef<HTMLDivElement[]>([])
	const translatedRefs = useRef<HTMLDivElement[]>([])
	const scrollSyncRafRef = useRef<number | null>(null)

	useEffect(() => {
		return () => {
			if (scrollSyncRafRef.current !== null) {
				cancelAnimationFrame(scrollSyncRafRef.current)
			}
		}
	}, [])

	const syncOriginalScroll = () => {
		if (!transcriptions) return

		const container = translatedRef.current
		if (!container) return

		const children = translatedRefs.current
		const containerTop = container.getBoundingClientRect().top

		let closestTranslationIndex = 0
		let minDistance = Infinity

		children.forEach((el, index) => {
			if (!el) return

			const rect = el.getBoundingClientRect()
			const distance = Math.abs(rect.top - containerTop)

			if (distance < minDistance) {
				minDistance = distance
				closestTranslationIndex = index
			}
		})

		const visibleTranslationChunk =
			transcriptions.transcriptions.translation[closestTranslationIndex]

		if (!visibleTranslationChunk) return

		const closestOriginalIndex = findClosestIndexByStart(
			transcriptions.transcriptions.original,
			visibleTranslationChunk.start
		)

		if (closestOriginalIndex < 0) return

		const target = originalRefs.current[closestOriginalIndex]

		if (target) {
			target.scrollIntoView({ behavior: 'auto', block: 'start' })
		}
	}

	const handleScroll = () => {
		if (scrollSyncRafRef.current !== null) return

		scrollSyncRafRef.current = requestAnimationFrame(() => {
			scrollSyncRafRef.current = null
			syncOriginalScroll()
		})
	}

	const scrollToSegment = useCallback(
		(segmentStart: number) => {
			if (!transcriptions) return

			const closestTranslationIndex = findClosestIndexByStart(
				transcriptions.transcriptions.translation,
				segmentStart
			)

			if (closestTranslationIndex < 0) return

			const target = translatedRefs.current[closestTranslationIndex]

			target?.scrollIntoView({
				behavior: 'auto',
				block: 'center'
			})
		},
		[transcriptions]
	)

	const collectOriginalRef = useCallback(
		(el: HTMLDivElement | null, index: number) => {
			if (el) originalRefs.current[index] = el
		},
		[]
	)

	const collectTranslatedRef = useCallback(
		(el: HTMLDivElement | null, index: number) => {
			if (el) translatedRefs.current[index] = el
		},
		[]
	)

	return {
		containerRef,
		translatedRef,
		collectOriginalRef,
		collectTranslatedRef,
		handleScroll,
		scrollToSegment
	}
}
